import { createContext, useContext, useEffect, useMemo, useState } from "react";

import Color from "color";

import { OpenAPI } from "simplydo/interfaces";

import { useMe } from "./useMe";

export const deviceSizes = {
  mobileS: 320,
  mobileM: 375,
  mobileL: 425,
  tablet: 768,
  laptop: 1024,
  laptopL: 1440,
  desktop: 2560,
};

const sat = 60;
const lum = 80;

export const colorMap = {
  red: `hsl(0deg ${sat}% ${lum}%)`,
  orange: `hsl(29deg ${sat}% ${lum}%)`,
  yellow: `hsl(43deg ${sat}% ${lum}%)`,
  green: `hsl(96deg ${sat}% ${lum}%)`,
  blue: `hsl(217deg ${sat}% ${lum}%)`,
  grey: "#e0e1e2",
  msteams: "#6264a7",
  violet: "#845fd4",
} as const;

// https://github.com/Qix-/color/blob/e188999dee229c902102ec37e398ff4d868616e5/index.js#L298
export const isDark = (color, threshold = 145) => {
  const rgb = color.rgb().color;
  const yiq = (rgb[0] * 2126 + rgb[1] * 7152 + rgb[2] * 722) / 10000;
  return yiq < threshold;
};

type UseThemeProps = {
  organisation?: {
    primaryColour?: string;
    secondaryColour?: string;
    fontFamily?: OpenAPI.Schemas["Organisation"]["fontFamily"];
  };
};

export type UseThemeType = {
  sizes: {
    isMobile: boolean;
    isTablet: boolean;
    isComputer: boolean;
    isLargeComputer: boolean;
    width: number;
    minStyle?: Record<string, string>;
    maxStyle?: Record<string, string>;
  };
  colorMap: typeof colorMap;
  primaryColour: string;
  secondaryColour: string;
  accent: string;
  secondaryAccent: string;
  shouldBeWhiteOnSecondary: boolean;
  shouldBeWhiteOnPrimary?: boolean;
  shouldBeWhiteOnAccent: boolean;
  shouldBeWhiteOnSecondaryAccent: boolean;
  boxShadow?: string;
  fontFamily?: OpenAPI.Schemas["Organisation"]["fontFamily"];
};

const SizeContext = createContext<UseThemeType["sizes"] | null>(null);

export const SizeProvider = ({ children }: { children: React.ReactNode }) => {
  const sizesFromWidth = (width: number): UseThemeType["sizes"] => {
    const isMobile = width < deviceSizes.tablet;
    const isTablet = width >= deviceSizes.tablet;
    const isComputer = width >= deviceSizes.laptop;
    const isLargeComputer = width >= deviceSizes.laptopL;
    return {
      isMobile,
      isTablet,
      isComputer,
      isLargeComputer,
      width,
      minStyle: Object.entries(deviceSizes).reduce(
        (curr, [key, val]) => ({ ...curr, [key]: `min-width: ${val}px;` }),
        {},
      ),
      maxStyle: Object.entries(deviceSizes).reduce(
        (curr, [key, val]) => ({ ...curr, [key]: `max-width: ${val}px;` }),
        {},
      ),
    };
  };

  const { innerWidth } = window;
  const [sizes, setSizes] = useState(sizesFromWidth(innerWidth));

  useEffect(() => {
    let timeout: number | null = null;
    const updateDimensions = () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        const { innerWidth: currentInnerWidth } = window;
        setSizes(sizesFromWidth(currentInnerWidth));
      }, 50);
    };

    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  return <SizeContext.Provider value={sizes}>{children}</SizeContext.Provider>;
};

export const loadGoogleFont = (
  fontFamily: string,
  weights: number[] = [400, 500, 600, 700],
) => {
  const fontId = `google-font-${fontFamily.replace(/\s+/g, "-")}`;

  // Prevent duplicate loads
  // Ensure that the html template sets the id of the main font on its link tag (e.g., id="google-font-Inter") to avoid double loading
  // Currently our preferred font is Inter
  if (document.getElementById(fontId)) return;

  const link = document.createElement("link");
  link.id = fontId;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
    /\s+/g,
    "+",
  )}:wght@${weights.join(";")}&display=swap`;
  document.head.appendChild(link);
};

export const useTheme = (props?: UseThemeProps) => {
  const sizes = useContext(SizeContext);
  if (!sizes) {
    throw new Error("useTheme must be used within a SizeProvider");
  }

  const me = useMe();
  const primaryColour =
    props?.organisation?.primaryColour ||
    me?.ownerOrganisation?.primaryColour ||
    "#14435B";
  const secondaryColour =
    props?.organisation?.secondaryColour ||
    me?.ownerOrganisation?.secondaryColour ||
    "#1b678e";
  const fontFamily = useMemo(
    () =>
      props?.organisation?.fontFamily ||
      me?.ownerOrganisation?.fontFamily || {
        h1: "Inter",
        h2: "Inter",
        h3: "Inter",
        h4: "Inter",
        h5: "Inter",
        body: "Inter",
      },
    [props?.organisation?.fontFamily, me?.ownerOrganisation?.fontFamily],
  );

  useEffect(() => {
    // For each unique font family, load the Google Font
    const fontFamilies = [
      fontFamily.h1,
      fontFamily.h2,
      fontFamily.h3,
      fontFamily.h4,
      fontFamily.h5,
      fontFamily.body,
    ];
    // @ts-ignore -- Set is iterable
    const uniqueFontFamilies = [...new Set(fontFamilies)];
    const validFontFamilies = uniqueFontFamilies.filter(Boolean);
    validFontFamilies.forEach((fontFamily) => {
      loadGoogleFont(fontFamily, [400, 500, 600, 700]);
    });
  }, [fontFamily]);

  const fullTheme = useMemo(() => {
    const accent = Color(primaryColour).lighten(0.2);
    const secondaryAccent = Color(secondaryColour).lighten(0.6).fade(0.8);

    return {
      sizes,
      primaryColour,
      secondaryColour,
      accent: accent.hex(),
      secondaryAccent: secondaryColour ? secondaryAccent.hex() : accent.hex(),
      shouldBeWhiteOnSecondary: isDark(Color(secondaryColour)),
      shouldBeWhiteOnPrimary: isDark(Color(primaryColour)),
      shouldBeWhiteOnAccent: isDark(accent),
      shouldBeWhiteOnSecondaryAccent: isDark(secondaryAccent),
      boxShadow: "0.5px 0.5px 2.5px 1px #C0C0C0",
      colorMap,
      fontFamily,
    };
  }, [sizes, primaryColour, secondaryColour, fontFamily]);

  return fullTheme;
};
